import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCompanyInfo } from "../../../../store/registrationSlice";
import { toast } from "react-hot-toast";
import ReactImageUploading from "react-images-uploading";
import ImageCropModal from "../../../../components/modals/ImageCropModal";
import Brand from "../../../../api/Brand";

const hearAboutList = [
  { value: "Social Media", label: "Social Media" },
  { value: "Google", label: "Google" },
  { value: "From a Friend", label: "From a Friend" },
  { value: "From an Ad", label: "From an Ad" },
  { value: "Others", label: "Others" },
];

const CompanyInformation = ({ nextStep, prevStep }) => {
  const dispatch = useDispatch();
  const savedCompanyInfo = useSelector((state) => state.registration.companyInfo);

  const [formData, setFormData] = useState({
    companyName: "",
    isEnterprise: false,
    hearAbout: "",
    otherSource: "",
    imageData: null,
  });

  const [errors, setErrors] = useState({});
  const [images, setImages] = useState([]);
  const [imageResponse, setImageResponse] = useState({});
  const [isUploadImage, setIsUploadImage] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [selectedFileData, setSelectedFileData] = useState("");
  const [crop, setCrop] = useState({
    unit: "%",
    width: 80,
    height: 80,
    x: 10,
    y: 10,
    aspect: 1,
  });
  const [completedCrop, setCompletedCrop] = useState(null);

  useEffect(() => {
    if (savedCompanyInfo) {
      setFormData(savedCompanyInfo);
      if (savedCompanyInfo.images && savedCompanyInfo.images.length > 0) {
        setImages(savedCompanyInfo.images);
      }
      if (savedCompanyInfo.imageData) {
        setImageResponse(savedCompanyInfo.imageData);
      }
    }
  }, [savedCompanyInfo]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName || !formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!formData.hearAbout) {
      newErrors.hearAbout = "Please select how you heard about us";
    } else if (
      formData.hearAbout === "Others" &&
      (!formData.otherSource || !formData.otherSource.trim())
    ) {
      newErrors.otherSource = "Please specify how you heard about us";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const setFieldValue = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openCropModal = (imageDataURL, selectedFile) => {
    setImageToCrop(imageDataURL);
    setSelectedFileData(selectedFile);
    setCrop({
      unit: "px",
      width: 100,
      height: 100,
      x: 10,
      y: 10,
      aspect: 1,
    });
    setShowCropModal(true);
  };

  const handleCropSave = async (croppedResult) => {
    if (croppedResult) {
      const croppedFile = new File(
        [croppedResult.file],
        selectedFileData?.name,
        { type: croppedResult.file.type }
      );
      let image = [
        {
          dataURL: croppedResult.url,
          file: croppedFile,
        },
      ];
      setShowCropModal(false);
      if (croppedFile) await uploadImage(image, croppedFile);
    }
  };

  const uploadImage = async (image, imageFile) => {
    if (!imageFile || imageFile.type === "image/svg+xml") {
      toast.error("SVG files are not allowed");
      return false;
    }
    try {
      setIsUploadImage(true);
      const response = await Brand.uploadBusinessLogo(imageFile);
      if (response) {
        setImageResponse(response?.data);
        setFieldValue("imageData", response?.data);
        setFieldValue("images", image);
        setImages(image);
        toast.success("Logo uploaded successfully");
        return true;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error uploading logo");
      return false;
    } finally {
      setIsUploadImage(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageToCrop(null);
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Include all image-related data in the company info
      const companyData = {
        ...formData,
        images: images,
        imageData: imageResponse,
        image: images[0]?.dataURL || null  // Use dataURL instead of file
      };
      dispatch(setCompanyInfo(companyData));
      nextStep();
    } else {
      toast.error("Please fill all required fields correctly");
    }
  };

  return (
    <div className="bg-white p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Company Information
      </h2>
      <div className="space-y-6">
        {/* Company Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md ${errors.companyName
              ? "border-red-500"
              : "border-gray-300 focus:border-primary-500"
              } focus:outline-none focus:ring-1 focus:ring-primary-500`}
            placeholder="Enter company name"
          />
          {errors.companyName && (
            <p className="mt-1 text-sm text-red-500">{errors.companyName}</p>
          )}
        </div>

        {/* Enterprise Toggle */}
        <div className="flex items-center space-x-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="isEnterprise"
              checked={formData.isEnterprise}
              onChange={handleChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
            <span className="ml-3 text-sm font-medium text-gray-700">
              Enterprise Account
            </span>
          </label>
        </div>

        {/* How did you hear about us */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            How did you hear about us?
          </label>
          <select
            name="hearAbout"
            value={formData.hearAbout}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md ${errors.hearAbout
              ? "border-red-500"
              : "border-gray-300 focus:border-primary-500"
              } focus:outline-none focus:ring-1 focus:ring-primary-500`}
          >
            <option value="">Select an option</option>
            {hearAboutList.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.hearAbout && (
            <p className="mt-1 text-sm text-red-500">{errors.hearAbout}</p>
          )}
        </div>

        {/* Other Source */}
        {formData.hearAbout === "Others" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Please specify
            </label>
            <input
              type="text"
              name="otherSource"
              value={formData.otherSource}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${errors.otherSource
                ? "border-red-500"
                : "border-gray-300 focus:border-primary-500"
                } focus:outline-none focus:ring-1 focus:ring-primary-500`}
              placeholder="Please tell us how you heard about us"
            />
            {errors.otherSource && (
              <p className="mt-1 text-sm text-red-500">{errors.otherSource}</p>
            )}
          </div>
        )}

        {/* Image Upload */}
        <div>
          <div className="relative">
            <ReactImageUploading
              value={images}
              onChange={(imageList) => {
                const selectedFile = imageList[0]?.file;
                if (selectedFile && selectedFile.type === "image/svg+xml") {
                  toast.error("SVG files are not allowed");
                } else {
                  if (imageList[0]) {
                    openCropModal(imageList[0].dataURL, selectedFile);
                  } else {
                    setImages([]);
                  }
                }
              }}
            >
              {({ imageList, onImageUpload, onImageRemove }) => (
                <div>
                  <div className="flex flex-col w-full">
                    {/* Main upload field with dotted border */}
                    <div className="relative border-2 border-gray-300 rounded-lg bg-gray-50">
                      <div
                        className={`text-sm text-gray-500 ${imageList.length > 0
                          ? "absolute duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white"
                          : "mx-4 my-2"
                          }`}
                      >
                        Upload Business Logo
                      </div>

                      {/* Dotted dropzone area */}
                      <div
                        onClick={onImageUpload}
                        className={`${imageList.length > 0 ? "mt-4" : "mb-4"
                          } mx-4 flex items-center justify-center h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-100`}
                      >
                        <div className="flex flex-col gap-1 items-center justify-center p-3">
                          <svg
                            className="w-8 h-8 text-gray-500"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 20 16"
                          >
                            <path
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                            />
                          </svg>
                          <span>
                            {isUploadImage && "Uploading..."}
                          </span>
                        </div>
                      </div>

                      {/* File preview area */}
                      {imageList.length > 0 && (
                        <div className="mx-4 mt-3 mb-4 flex items-center justify-between bg-white">
                          <div className="flex items-center">
                            <img
                              src={imageList[0].dataURL}
                              alt=""
                              className="h-10 w-10 object-cover rounded-md mr-3"
                            />
                            <div>
                              <p className="text-xs text-gray-900">
                                {imageList[0].file?.name || "Uploaded Logo"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {imageList[0].file?.size
                                  ? `${(
                                    imageList[0].file.size / 1024
                                  ).toFixed(1)} KB`
                                  : ""}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              onImageRemove(0);
                              setImages([]);
                              setImageResponse({});
                              setFieldValue("images", []);
                              setFieldValue("imageData", null);
                            }}
                            className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                            type="button"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </ReactImageUploading>
          </div>
          <p className="text-gray-500">
            Don't have a logo handy? You can do it later
          </p>
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-end space-x-3">
          <button
            onClick={prevStep}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Previous
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-500 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Next
          </button>
        </div>
      </div>

      <ImageCropModal
        showCropModal={showCropModal}
        handleCropSave={handleCropSave}
        handleCropCancel={handleCropCancel}
        imageToCrop={imageToCrop}
        crop={crop}
        setCrop={setCrop}
        completedCrop={completedCrop}
        setCompletedCrop={setCompletedCrop}
      />
    </div>
  );
};

export default CompanyInformation;